import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SnackbarWrapper from "components/Snackbar";
import LoadingScreen from "components/Loading";
import { useAuth } from "context/authContext";
import {
  getJobs,
  getPayrollStructuresByJobId,
  getEmployeesWithoutCompensation,
  bulkAssignCompensation,
} from "services/api";

const BulkOnboardView = ({ canEdit }) => {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const snackbarRef = useRef();

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const [selectedJob, setSelectedJob] = useState(null);
  const [structuresForJob, setStructuresForJob] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));

  const [uncoveredEmployees, setUncoveredEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadJobs = async () => {
      setLoadingJobs(true);
      const res = await getJobs();
      if (res !== "error") setJobs(res);
      setLoadingJobs(false);
    };
    loadJobs();
  }, []);

  const handleSelectJob = async (job) => {
    setSelectedJob(job);
    setSelectedStructure(null);
    setSelectedEmployeeIds([]);
    setStructuresForJob([]);
    setUncoveredEmployees([]);

    if (!job) return;

    setLoadingEmployees(true);
    const [structuresRes, employeesRes] = await Promise.all([
      getPayrollStructuresByJobId(job.jobId),
      getEmployeesWithoutCompensation(job.jobId),
    ]);
    if (structuresRes !== "error") setStructuresForJob(structuresRes);
    if (employeesRes !== "error") setUncoveredEmployees(employeesRes);
    setLoadingEmployees(false);
  };

  const toggleEmployee = (employeeId) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedEmployeeIds.length === uncoveredEmployees.length) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(uncoveredEmployees.map((e) => e.id));
    }
  };

  const canSubmit =
    !!selectedJob &&
    !!selectedStructure &&
    !!effectiveFrom &&
    selectedEmployeeIds.length > 0 &&
    canEdit;

  const handleSubmit = async () => {
    setSubmitting(true);

    const res = await bulkAssignCompensation({
      payrollStructureId: selectedStructure.id,
      employeeIds: selectedEmployeeIds,
      currency: selectedStructure.currency,
      effectiveFrom,
      approvedBy: employee?.id,
      createdBy: employee?.id,
    });

    if (res && typeof res === "object" && Array.isArray(res.created)) {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.employees.bulkOnboardSuccess", { count: res.created.length }),
        "success"
      );
      // Refresh the uncovered list - anyone just onboarded should drop off it
      const refreshed = await getEmployeesWithoutCompensation(selectedJob.jobId);
      if (refreshed !== "error") setUncoveredEmployees(refreshed);
      setSelectedEmployeeIds([]);
    } else {
      snackbarRef.current?.showSnackbar(
        typeof res === "string" ? res : t("admin.payroll.employees.bulkOnboardError"),
        "error"
      );
    }
    setSubmitting(false);
  };

  if (loadingJobs) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(job) => job.jobName || ""}
              isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
              value={selectedJob}
              onChange={(event, newValue) => handleSelectJob(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t("admin.payroll.employees.job")} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={structuresForJob}
              getOptionLabel={(s) => s.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedStructure}
              onChange={(event, newValue) => setSelectedStructure(newValue)}
              disabled={!selectedJob}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.payroll.employees.structureToAssign")}
                  helperText={
                    selectedJob && structuresForJob.length === 0
                      ? t("admin.payroll.employees.noStructuresForJob")
                      : ""
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={t("admin.payroll.employees.effectiveFrom")}
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {selectedJob && (
          <>
            {loadingEmployees ? (
              <LoadingScreen />
            ) : uncoveredEmployees.length === 0 ? (
              <MDTypography variant="body2" color="text" sx={{ py: 3, textAlign: "center" }}>
                {t("admin.payroll.employees.allOnboarded")}
              </MDTypography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: 40 }}>
                        <Checkbox
                          checked={
                            uncoveredEmployees.length > 0 &&
                            selectedEmployeeIds.length === uncoveredEmployees.length
                          }
                          indeterminate={
                            selectedEmployeeIds.length > 0 &&
                            selectedEmployeeIds.length < uncoveredEmployees.length
                          }
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("admin.payroll.employees.employee")}
                      </TableCell>
                    </TableRow>
                    {uncoveredEmployees.map((emp) => (
                      <TableRow
                        key={emp.id}
                        hover
                        onClick={() => toggleEmployee(emp.id)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedEmployeeIds.includes(emp.id)}
                            onChange={() => toggleEmployee(emp.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>{emp.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <MDBox sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <MDButton color="primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                {t("admin.payroll.employees.onboardSelected", {
                  count: selectedEmployeeIds.length,
                })}
              </MDButton>
            </MDBox>
          </>
        )}
      </Paper>

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

BulkOnboardView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default BulkOnboardView;
